import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
} from "@mui/material";
import { toast } from "react-toastify";
import api from "../../api";

const statusColor = (status) => {
  switch (status) {
    case "new":
      return "info";
    case "in_progress":
      return "warning";
    case "closed":
      return "default";
    default:
      return "default";
  }
};

const MobileRevisionSupportRequests = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [requests, setRequests] = useState([]);
  const [tutors, setTutors] = useState([]);
  const [selected, setSelected] = useState(null);
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState("new");
  const [assignedTutorId, setAssignedTutorId] = useState("");

  const canOpenDialog = useMemo(() => !!selected, [selected]);

  const fetchRequests = async () => {
    try {
      const res = await api.get("admin/mobile-revision-support-requests");
      if (res.data?.success) setRequests(res.data.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.message || "Failed to load mobile revision support requests.");
    } finally {
      setLoading(false);
    }
  };

  const fetchTutors = async () => {
    try {
      const res = await api.get("admin/tutors", { params: { page: 1, per_page: 500 } });
      if (res.data?.success) setTutors(res.data.data?.data || []);
    } catch (e) {
      console.error("Failed to load tutors:", e);
    }
  };

  useEffect(() => {
    fetchRequests();
    fetchTutors();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openRow = (row) => {
    setSelected(row);
    setStatus(row.status ?? "new");
    setAssignedTutorId(row.assigned_tutor_id != null ? String(row.assigned_tutor_id) : "");
    setOpen(true);
  };

  const closeDialog = () => {
    setOpen(false);
    setSelected(null);
  };

  const handleSave = async () => {
    if (!selected?.id) return;
    setSaving(true);
    try {
      await api.patch(`admin/mobile-revision-support-requests/${selected.id}`, {
        status,
        assigned_tutor_id: assignedTutorId === "" ? null : Number(assignedTutorId),
      });
      toast.success("Request updated. Tutor and student will be notified when a tutor is assigned.");
      await fetchRequests();
      closeDialog();
    } catch (e) {
      toast.error(e?.response?.data?.message || "Failed to update request.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ p: 3, minHeight: "100vh", background: "linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)" }}>
      <Typography variant="h5" fontWeight={800} sx={{ mb: 1 }}>
        Mobile revision support
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Tutor requests submitted from the student mobile app. Assign a tutor to notify them and the student by email and in-app notification.
      </Typography>

      <Paper sx={{ p: 2 }}>
        {loading ? (
          <Box sx={{ py: 6, display: "flex", justifyContent: "center" }}>
            <CircularProgress />
          </Box>
        ) : requests.length === 0 ? (
          <Typography color="text.secondary" sx={{ py: 3 }}>
            No mobile revision support requests yet.
          </Typography>
        ) : (
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ background: "#f8f9ff" }}>
                  <TableCell>ID</TableCell>
                  <TableCell>Student</TableCell>
                  <TableCell>Registered email</TableCell>
                  <TableCell>Subject</TableCell>
                  <TableCell>Tutor</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Submitted</TableCell>
                  <TableCell align="right">Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {requests.map((r) => (
                  <TableRow key={r.id} hover>
                    <TableCell>{r.id}</TableCell>
                    <TableCell sx={{ maxWidth: 160 }}>
                      <Typography variant="body2" fontWeight={700}>
                        {r.student_name}
                      </Typography>
                      {r.mobile_user_id != null ? (
                        <Typography variant="caption" color="text.secondary">
                          Mobile user #{r.mobile_user_id}
                        </Typography>
                      ) : null}
                    </TableCell>
                    <TableCell sx={{ maxWidth: 200 }}>{r.registered_email}</TableCell>
                    <TableCell>{r.subject}</TableCell>
                    <TableCell sx={{ maxWidth: 140 }}>
                      {r.assigned_tutor?.name ? (
                        <Typography variant="body2" noWrap title={r.assigned_tutor.name}>
                          {r.assigned_tutor.name}
                        </Typography>
                      ) : (
                        <Typography variant="caption" color="text.secondary">
                          —
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip size="small" label={r.status} color={statusColor(r.status)} variant="outlined" />
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption">
                        {r.created_at ? new Date(r.created_at).toLocaleString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : "—"}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Button size="small" variant="outlined" onClick={() => openRow(r)}>
                        View
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      <Dialog open={open && canOpenDialog} onClose={closeDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Mobile revision support #{selected?.id}</DialogTitle>
        <DialogContent dividers>
          {selected ? (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, pt: 1 }}>
              <Typography variant="body2">
                <strong>Student:</strong> {selected.student_name}
              </Typography>
              <Typography variant="body2">
                <strong>Registered email:</strong> {selected.registered_email}
              </Typography>
              {selected.parent_email ? (
                <Typography variant="body2">
                  <strong>Parent email:</strong> {selected.parent_email}
                </Typography>
              ) : null}
              <Typography variant="body2">
                <strong>Subject:</strong> {selected.subject}
              </Typography>
              <Typography variant="body2">
                <strong>Topic / focus:</strong> {selected.focus}
              </Typography>
              {selected.preferred_day_time ? (
                <Typography variant="body2">
                  <strong>Preferred day &amp; time:</strong> {selected.preferred_day_time}
                </Typography>
              ) : null}
              {selected.additional_notes ? (
                <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                  <strong>Notes:</strong> {selected.additional_notes}
                </Typography>
              ) : null}

              <FormControl fullWidth size="small" sx={{ mt: 1 }}>
                <InputLabel id="revision-tutor-label">Assign tutor</InputLabel>
                <Select
                  labelId="revision-tutor-label"
                  label="Assign tutor"
                  value={assignedTutorId}
                  onChange={(e) => setAssignedTutorId(e.target.value)}
                >
                  <MenuItem value="">
                    <em>Not assigned</em>
                  </MenuItem>
                  {tutors.map((t) => (
                    <MenuItem key={t.id} value={String(t.id)}>
                      {t.full_name || `${t.first_name || ""} ${t.last_name || ""}`.trim() || `Tutor #${t.id}`}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select label="Status" value={status} onChange={(e) => setStatus(e.target.value)}>
                  <MenuItem value="new">new</MenuItem>
                  <MenuItem value="in_progress">in_progress</MenuItem>
                  <MenuItem value="closed">closed</MenuItem>
                </Select>
              </FormControl>

              <Typography variant="caption" color="text.secondary">
                Changing the assigned tutor sends email and in-app notifications to the tutor and student (mobile app when linked).
              </Typography>
            </Box>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog}>Close</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? "Saving…" : "Save"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MobileRevisionSupportRequests;
